import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, UserPlus, Users as UsersIcon, Save, Crown, Shield } from 'lucide-react';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { confirmDelete, swalConfirm, swalError } from '../common/confirm';
import {
    Agency, AgencyMember, AgencyMemberCreatePayload, AgencyHead, AgencyLinkCandidate,
    agencyMemberService,
} from '../../services/auditService';
import { userService } from '../../services/userService';
import { extractApiError } from '../../utils/apiError';

interface AgencyMembersModalProps {
    isOpen: boolean;
    onClose: () => void;
    agency: Agency | null;
    onChanged?: () => void;
}

type AddMode = null | 'existing' | 'new' | 'designate-head';

const blankNew = {
    first_name: '',
    last_name: '',
    login_id: '',
    email: '',
    phone: '',
    password: 'changeme123',
};

const AgencyMembersModal: React.FC<AgencyMembersModalProps> = ({ isOpen, onClose, agency, onChanged }) => {
    // Approved agency = frozen membership (no add/remove). Reopen the agency from its status dot first.
    const agencyApproved = (agency as any)?.workflow_state === 'approved';
    const [members, setMembers] = useState<AgencyMember[]>([]);
    const [head, setHead] = useState<AgencyHead | null>(null);
    const [candidates, setCandidates] = useState<AgencyLinkCandidate[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [addMode, setAddMode] = useState<AddMode>(null);
    const [existingUserId, setExistingUserId] = useState<number | ''>('');
    const [newMember, setNewMember] = useState({ ...blankNew });

    const reload = useCallback(async () => {
        if (!agency) return;
        setLoading(true);
        setError(null);
        try {
            const [m, h] = await Promise.all([
                agencyMemberService.listForAgency(agency.id),
                agencyMemberService.getHead(agency.id),
            ]);
            setMembers(m);
            setHead(h);
            // Kind-filtered eligible users (External agency → External Auditors
            // only; Internal → internal staff). Already excludes current members.
            try {
                const c = await agencyMemberService.linkCandidates(agency.id);
                setCandidates(c || []);
            } catch {
                setCandidates([]);
            }
        } catch (e: any) {
            setError(extractApiError(e, 'Failed to load members.'));
        } finally {
            setLoading(false);
        }
    }, [agency]);

    useEffect(() => {
        if (isOpen && agency) {
            reload();
            setAddMode(null);
            setExistingUserId('');
            setNewMember({ ...blankNew });
        }
    }, [isOpen, agency, reload]);

    // Add-existing: eligible (kind-filtered) users not already members.
    const userOptions = candidates.map(c => ({
        value: c.user_id,
        label: c.name || c.email || `User #${c.user_id}`,
    }));
    // Designate-head: current members (promote) PLUS eligible candidates, deduped.
    const memberHeadOptions = members.map(m => ({
        value: m.user_id,
        label: `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.login_id || `User #${m.user_id}`,
    }));
    const seenHead = new Set(memberHeadOptions.map(o => o.value));
    const headCandidateOptions = [
        ...memberHeadOptions,
        ...userOptions.filter(o => !seenHead.has(o.value)),
    ];

    const formatMember = (m: AgencyMember) => {
        const name =
            `${m.first_name || ''} ${m.last_name || ''}`.trim()
            || m.login_id
            || `User #${m.user_id}`;
        return { name, contact: m.email || m.login_id || '' };
    };

    const submitAdd = async () => {
        if (!agency) return;
        try {
            let userId: number;
            if (addMode === 'existing') {
                if (!existingUserId) return;
                userId = Number(existingUserId);
            } else if (addMode === 'new') {
                const fn = newMember.first_name.trim();
                const ln = newMember.last_name.trim();
                const lid = newMember.login_id.trim();
                const em = newMember.email.trim();
                const pw = newMember.password;
                if (!fn || !ln || !lid || !em || !pw) return;
                const created = await userService.createUser({
                    first_name: fn, last_name: ln, login_id: lid, email: em, password: pw,
                    phone: newMember.phone.trim() || undefined,
                });
                if (!created?.id) throw new Error('User creation did not return an id.');
                userId = created.id;
            } else {
                return;
            }
            const payload: AgencyMemberCreatePayload = {
                agency_id: agency.id, user_id: userId,
                member_role: 'Staff', is_active: 1, is_approved: 0,
            };
            await agencyMemberService.create(payload);
            setAddMode(null);
            setExistingUserId('');
            setNewMember({ ...blankNew });
            await reload();
            onChanged && onChanged();
        } catch (e: any) {
            swalError(extractApiError(e, 'Failed to add member.'));
        }
    };

    const submitDesignateHead = async () => {
        if (!agency || !existingUserId) return;
        try {
            await agencyMemberService.designateHead(agency.id, Number(existingUserId));
            setAddMode(null);
            setExistingUserId('');
            await reload();
            onChanged && onChanged();
        } catch (e: any) {
            swalError(extractApiError(e, 'Designation failed.'));
        }
    };

    const removeMember = async (memberId: number, isHead: boolean) => {
        const title = isHead
            ? 'Remove Agency Head?'
            : 'Remove Member?';
        const text = isHead
            ? 'This member is the Agency Head. Removing them will leave the Agency without a Head.'
            : '';
        const confirmed = await confirmDelete({ title, text });
        if (!confirmed) return;
        try {
            await agencyMemberService.remove(memberId);
            await reload();
            onChanged && onChanged();
        } catch (e: any) {
            swalError(extractApiError(e, 'Failed to remove member.'));
        }
    };

    const canSubmitNew = !!(
        newMember.first_name.trim() && newMember.last_name.trim() &&
        newMember.login_id.trim() && newMember.email.trim() && newMember.password
    );

    const headName = head
        ? `${head.first_name || ''} ${head.last_name || ''}`.trim() || head.login_id || `User #${head.user_id}`
        : null;

    return (
        <>
            <Modal isOpen={isOpen && !!agency} onClose={onClose} size="md">
                <Modal.Header>
                    <span>
                        Team
                        {agency && <span className="block text-sm font-normal text-gray-500 mt-0.5">{agency.audit_agency_name}</span>}
                    </span>
                </Modal.Header>
                <Modal.Body>
                    {error && <div className="mb-3 p-2 text-sm border border-red-200 bg-red-50 text-red-700 rounded">{error}</div>}

                    {/* Agency Head banner — BRD#748 */}
                    <div className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-4 border ${head ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
                        <Crown size={18} className={head ? 'text-amber-500 shrink-0' : 'text-gray-300 shrink-0'} />
                        <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-0.5">Agency Head</div>
                            {head ? (
                                <div className="text-sm font-medium text-gray-800 truncate">
                                    {headName}
                                    {head.email && <span className="ml-2 text-xs text-gray-500">{head.email}</span>}
                                </div>
                            ) : (
                                <div className="text-sm text-gray-400 italic">No Agency Head designated yet</div>
                            )}
                        </div>
                        <button
                            type="button"
                            data-testid="BTN_agency_members_designate_head_open"
                            onClick={() => { setExistingUserId(''); setAddMode('designate-head'); }}
                            className="shrink-0 text-xs px-3 py-1.5 rounded border border-amber-300 bg-amber-100 hover:bg-amber-200 text-amber-800 font-medium flex items-center gap-1"
                        >
                            <Crown size={11} /> {head ? 'Replace Head' : 'Designate Head'}
                        </button>
                    </div>

                    {/* Members list */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs uppercase font-bold text-gray-500 tracking-wider">
                            Audit Team ({members.length})
                        </div>
                        <div className="divide-y divide-gray-100">
                            {loading && <div className="p-4 text-sm text-gray-400 text-center">Loading…</div>}
                            {!loading && members.map(m => {
                                const f = formatMember(m);
                                const isHead = m.member_role === 'Agency Head';
                                return (
                                    <div key={m.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                                        <div className="overflow-hidden flex items-center gap-2">
                                            {isHead && <Crown size={13} className="text-amber-500 shrink-0" aria-label="Agency Head" />}
                                            {!isHead && <Shield size={13} className="text-gray-300 shrink-0" aria-label="Staff" />}
                                            <div>
                                                <div className="text-sm font-medium text-gray-800 truncate">
                                                    {f.name || '—'}
                                                    {isHead && (
                                                        <span className="ml-2 status-badge xs warning">Head</span>
                                                    )}
                                                </div>
                                                {f.contact && <div className="text-xs text-gray-500 truncate">{f.contact}</div>}
                                            </div>
                                        </div>
                                        {!agencyApproved && (
                                            <button
                                                type="button"
                                                data-testid={`BTN_agency_members_row_${m.id}_remove`}
                                                onClick={() => removeMember(m.id, isHead)}
                                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                            {!loading && members.length === 0 && (
                                <div className="p-6 text-center text-sm text-gray-400 italic">No team members yet.</div>
                            )}
                        </div>
                    </div>

                    {agencyApproved ? (
                        <p style={{ fontSize: 13, color: '#64748b', fontStyle: 'italic' }} data-testid="LBL_agency_members_readonly">
                            Agency is approved — membership is read-only. Reopen the agency from its status dot to change members.
                        </p>
                    ) : (
                        <div className="flex gap-2">
                            <button type="button" data-testid="BTN_agency_members_add_existing" onClick={() => setAddMode('existing')} className="flex-1 flex items-center justify-center gap-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 px-3 py-2 rounded text-sm">
                                <UsersIcon size={14} /> Add Existing User
                            </button>
                            <button type="button" data-testid="BTN_agency_members_create_new" onClick={() => setAddMode('new')} className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm">
                                <UserPlus size={14} /> Create New Member
                            </button>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline" type="button" data-testid="BTN_agency_members_done" onClick={onClose}>Done</Button>
                </Modal.Footer>
            </Modal>

            {/* Designate Agency Head sub-modal — BRD#748 / BRD#750 */}
            <Modal isOpen={addMode === 'designate-head'} onClose={() => { setAddMode(null); setExistingUserId(''); }} size="sm">
                <Modal.Header>
                    <span className="flex items-center gap-2"><Crown size={16} className="text-amber-500" /> Designate Agency Head</span>
                </Modal.Header>
                <Modal.Body>
                    <FormControl.SearchSelect
                        label="User"
                        required
                        data-testid="SEL_agency_members_designate_head_user"
                        options={headCandidateOptions}
                        value={existingUserId || null}
                        onChange={v => setExistingUserId(v ? Number(v) : '')}
                        placeholder="— Select user —"
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline" type="button" data-testid="BTN_agency_members_designate_head_cancel" onClick={() => { setAddMode(null); setExistingUserId(''); }}>Cancel</Button>
                    <Button
                        variant="fill"
                        icon={Crown}
                        type="button"
                        data-testid="BTN_agency_members_designate_head_submit"
                        onClick={submitDesignateHead}
                        disabled={!existingUserId}
                    >
                        Designate Head
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add Existing User sub-modal */}
            <Modal isOpen={addMode === 'existing'} onClose={() => { setAddMode(null); setExistingUserId(''); }} size="sm">
                <Modal.Header>Add Existing User</Modal.Header>
                <Modal.Body>
                    <FormControl.SearchSelect
                        label="User"
                        required
                        data-testid="SEL_agency_members_existing_user"
                        options={userOptions}
                        value={existingUserId || null}
                        onChange={v => setExistingUserId(v ? Number(v) : '')}
                        placeholder="— Select user —"
                        helper={userOptions.length === 0 ? 'No eligible users available.' : undefined}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline" type="button" data-testid="BTN_agency_members_add_existing_cancel" onClick={() => { setAddMode(null); setExistingUserId(''); }}>Cancel</Button>
                    <Button variant="fill" icon={Plus} type="button" data-testid="BTN_agency_members_add_existing_submit" onClick={submitAdd} disabled={!existingUserId}>
                        Add Member
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Create New User sub-modal */}
            <Modal isOpen={addMode === 'new'} onClose={() => { setAddMode(null); setNewMember({ ...blankNew }); }} size="md">
                <Modal.Header>Create New User &amp; Add to Agency</Modal.Header>
                <Modal.Body>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        <FormControl.Input label="First Name" required data-testid="INP_agency_members_new_first_name" value={newMember.first_name} onChange={e => setNewMember({ ...newMember, first_name: e.target.value })} />
                        <FormControl.Input label="Last Name" required data-testid="INP_agency_members_new_last_name" value={newMember.last_name} onChange={e => setNewMember({ ...newMember, last_name: e.target.value })} />
                        <FormControl.Input label="Login ID" required data-testid="INP_agency_members_new_login_id" placeholder="e.g. siva.prasad" value={newMember.login_id} onChange={e => setNewMember({ ...newMember, login_id: e.target.value })} />
                        <FormControl.Input label="Email" required type="email" data-testid="INP_agency_members_new_email" value={newMember.email} onChange={e => setNewMember({ ...newMember, email: e.target.value })} />
                        <FormControl.Input label="Phone" data-testid="INP_agency_members_new_phone" value={newMember.phone} onChange={e => setNewMember({ ...newMember, phone: e.target.value })} />
                        <FormControl.Input label="Initial Password" required data-testid="INP_agency_members_new_password" value={newMember.password} onChange={e => setNewMember({ ...newMember, password: e.target.value })} />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline" type="button" data-testid="BTN_agency_members_new_cancel" onClick={() => { setAddMode(null); setNewMember({ ...blankNew }); }}>Cancel</Button>
                    <Button variant="fill" icon={Save} type="button" data-testid="BTN_agency_members_new_submit" onClick={submitAdd} disabled={!canSubmitNew}>
                        Create &amp; Add
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default AgencyMembersModal;
